import React, { useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import { patchCustomer } from "../../../../../shared/services/services";
import { CustomerData } from "../../../../../shared/services/interface";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import PopupModules from "src/features/auth/components/PopupModules";
import { textColorOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { errorHandling } from "src/shared/services/errorHandling";
import InputGroup from "./InputGroup";

type Props = {
  close: (data: any) => void;
  prvCustomer: CustomerData;
  loading: (data: boolean) => void;
  editCustomer: boolean;
};

const EditCustomer: React.FC<Props> = ({ close, prvCustomer, loading, editCustomer }) => {
  const [formData, setFormData] = useState({
    name: prvCustomer.Name || "",
    phoneNumber: prvCustomer.phone_number || "",
    alternativenumber: prvCustomer.alternativenumber || "",
    Email: prvCustomer.Email || "",
    customeraddress1: prvCustomer.Address.customeraddress1 || "",
    customeraddress2: prvCustomer.Address.customeraddress2 || "",
    customeraddress3: prvCustomer.Address.customeraddress3 || "",
  });
  const [showMore, setShowMore] = useState(false);
  const { user } = useUser();
  const handleChange = (name: string, value: string) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleConfirm = async () => {
    const { phoneNumber } = formData;

    if (!phoneNumber.trim()) {
      notifyError(" primary number are required.");
      return;
    }

    const updateData: any = {};

    // Compare and add only changed values
    if (formData.name !== prvCustomer.Name) updateData.Name = formData.name;
    if (formData.Email !== prvCustomer.Email) updateData.Email = formData.Email;
    if (formData.phoneNumber !== prvCustomer.phone_number) updateData.phone_number = formData.phoneNumber;
    if (formData.alternativenumber !== prvCustomer.alternativenumber) updateData.alternativenumber = formData.alternativenumber;

    // Compare address fields
    const newAddress: any = {};
    if (formData.customeraddress1 !== prvCustomer.Address?.customeraddress1) newAddress.customeraddress1 = formData.customeraddress1;
    if (formData.customeraddress2 !== prvCustomer.Address?.customeraddress2) newAddress.customeraddress2 = formData.customeraddress2;
    if (formData.customeraddress3 !== prvCustomer.Address?.customeraddress3) newAddress.customeraddress3 = formData.customeraddress3;

    if (Object.keys(newAddress).length > 0) updateData.Address = newAddress;

    try {
      loading(true);
      const res = await patchCustomer(prvCustomer._id.$oid, updateData);
      notifySuccess("Customer updated successfully!");
      close(res);
    } catch (err) {
      console.error(err);
      errorHandling(err);
    } finally {
      loading(false);
    }
  };

  const isDisabled = formData.phoneNumber.length < 8 || formData.phoneNumber.length > 12 || formData.phoneNumber === formData.alternativenumber;

  return (
    <PopupModules isOpen={editCustomer} onClose={() => close("")} title='Edit Customer'>
      <div className='flex flex-col gap-3 p-2 sm:p-4'>
        <InputGroup label='Customer Name' type='text' name='name' value={formData.name} onChange={handleChange} />
        <InputGroup
          label='Primary Number (8 to 12 digits on numbers)'
          type='number'
          name='phoneNumber'
          value={formData.phoneNumber}
          onChange={handleChange}
          mandatory
        />

        <div className='w-full flex justify-end mt-1'>
          <button
            onClick={() => setShowMore(!showMore)}
            className={`text-sm font-medium hover:underline ${textColorOptions[user?.background_colour ?? "Blue"]}`}
          >
            {showMore ? "Hide Details ▲" : "More Details ▼"}
          </button>
        </div>

        {showMore && (
          <div className='border-t border-gray-200 pt-3 mt-2 space-y-3'>
            <InputGroup label='Email ID' type='email' name='Email' value={formData.Email} onChange={handleChange} />
            <InputGroup
              label='Secondary Number'
              type='number'
              name='alternativenumber'
              value={formData.alternativenumber}
              onChange={handleChange}
              error={formData.phoneNumber === formData.alternativenumber ? true : false}
            />
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <InputGroup label='Address 1' type='text' name='customeraddress1' value={formData.customeraddress1} onChange={handleChange} />
              <InputGroup label='Address 2' type='text' name='customeraddress2' value={formData.customeraddress2} onChange={handleChange} />
              <InputGroup label='Address 3' type='text' name='customeraddress3' value={formData.customeraddress3} onChange={handleChange} />
            </div>
          </div>
        )}

        {formData.phoneNumber === formData.alternativenumber && (
          <p className='text-red-600 text-sm'>Primary and Secondary numbers cannot be the same.</p>
        )}

        <div className='flex justify-center gap-4 pt-4 border-t border-gray-200 mt-3'>
          <Button label='Cancel' variant='ThemeCancel' className='border' onClick={() => close("")} />
          <Button label='Confirm' variant='ThemeBase' onClick={handleConfirm} disabled={isDisabled} />
        </div>
      </div>
    </PopupModules>
  );
};

export default EditCustomer;
